import React, { useEffect, useRef, useState } from 'react';
import { useMessageStore } from '../../store/messageStore';
import { useAuthStore } from '../../store/authStore';
import { useFriendStore } from '../../store/friendStore';
import { format, isToday, isYesterday, differenceInCalendarDays, startOfDay, isValid } from 'date-fns';
import { Edit, Trash2, Smile, Plus } from 'lucide-react';
import MessageInput from './MessageInput';
import UserSettingsModal from '../settings/UserSettingsModal';
import EmojiPicker from 'emoji-picker-react';
import TypingIndicator from './TypingIndicator';
import { getChannel, joinDirectMessage as joinDMChannel } from '../../lib/realtime';
import { hydrateMessageAuthor } from '../../lib/messageAuthors';
import { applyMessageUpdate, applyReactionUpdate, normalizeMessage } from '../../lib/messageEvents';

function parseMessageDate(dateStr?: string | null): Date {
  const date = new Date(dateStr || '');
  return isValid(date) ? date : new Date();
}

function formatRelativeDate(dateStr?: string | null): { label: string; tooltip: string } {
  const date = parseMessageDate(dateStr);
  const now = new Date();
  const tooltip = format(date, 'dd/MM/yyyy HH:mm:ss');

  if (isToday(date)) {
    return { label: format(date, 'h:mm a'), tooltip };
  }
  if (isYesterday(date)) {
    return { label: `Yesterday at ${format(date, 'h:mm a')}`, tooltip };
  }
  const daysDiff = differenceInCalendarDays(startOfDay(now), startOfDay(date));
  if (daysDiff < 7) {
    return { label: `${format(date, 'EEEE')} at ${format(date, 'h:mm a')}`, tooltip };
  }
  return { label: format(date, 'MMM d, yyyy h:mm a'), tooltip };
}
import MessageProcessor from './MessageProcessor';
import DiscordEmbeds from './DiscordEmbeds';
import UserProfileLink from '../profile/UserProfileLink';
import AppBadge from '../ui/AppBadge';

const PAGE_SIZE = 40;
const TOP_LOAD_THRESHOLD = 80;
const BOTTOM_STICK_THRESHOLD = 120;

const DirectMessageChat: React.FC = () => {
  const { currentDmUser } = useMessageStore();
  const { user } = useAuthStore();
  const { friends } = useFriendStore();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState<string | undefined>();
  const [reactionPickerMessageId, setReactionPickerMessageId] = useState<string | null>(null);
  const reactionPickerRef = useRef<HTMLDivElement>(null);
  const [localDmMessages, setLocalDmMessages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const dmUserRef = useRef<string | null>(null);
  const initialScrollDoneRef = useRef(false);
  const shouldStickToBottomRef = useRef(true);

  const scrollToBottom = (behavior: ScrollBehavior = 'auto') => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior });
    shouldStickToBottomRef.current = true;
  };

  const loadOlderMessages = async () => {
    if (!currentDmUser || !user || isLoading || isLoadingMore || !hasMore || localDmMessages.length === 0) {
      return;
    }

    const oldestMessage = localDmMessages[0];
    if (!oldestMessage?.created_at) {
      setHasMore(false);
      return;
    }

    const container = scrollContainerRef.current;
    const previousHeight = container?.scrollHeight ?? 0;
    setIsLoadingMore(true);

    try {
      const olderMessages = await useMessageStore
        .getState()
        .messagesAPI.getDirectMessages(currentDmUser, {
          limit: PAGE_SIZE,
          before: oldestMessage.id,
        });

      const batch = (olderMessages || []).map(normalizeMessage);
      setHasMore(batch.length === PAGE_SIZE);

      setLocalDmMessages((prev) => {
        const existingIds = new Set(prev.map((message) => message.id));
        const dedupedBatch = batch.filter((message: any) => !existingIds.has(message.id));
        return dedupedBatch.length > 0 ? [...dedupedBatch, ...prev] : prev;
      });

      requestAnimationFrame(() => {
        const currentContainer = scrollContainerRef.current;
        if (!currentContainer) return;
        const newHeight = currentContainer.scrollHeight;
        currentContainer.scrollTop += newHeight - previousHeight;
      });
    } catch {
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const distanceFromBottom =
      container.scrollHeight - (container.scrollTop + container.clientHeight);
    shouldStickToBottomRef.current = distanceFromBottom <= BOTTOM_STICK_THRESHOLD;

    if (container.scrollTop <= TOP_LOAD_THRESHOLD) {
      void loadOlderMessages();
    }
  };

  // Effect 1: fetch DMs when conversation partner changes
  useEffect(() => {
    if (!user || !currentDmUser) return;
    if (dmUserRef.current === currentDmUser) return;

    dmUserRef.current = currentDmUser;
    initialScrollDoneRef.current = false;
    shouldStickToBottomRef.current = true;
    setLocalDmMessages([]);
    setHasMore(true);
    setIsLoadingMore(false);
    setIsLoading(true);

    const messageStore = useMessageStore.getState();
    messageStore.joinDirectMessage(currentDmUser);

    messageStore.messagesAPI.getDirectMessages(currentDmUser, { limit: PAGE_SIZE })
      .then(messages => {
        const batch = (messages || []).map(normalizeMessage);
        setLocalDmMessages(batch);
        setHasMore(batch.length === PAGE_SIZE);
      })
      .catch(() => { setLocalDmMessages([]); })
      .finally(() => { setIsLoading(false); });
  }, [currentDmUser, user?.id]);

  // Effect 2: Phoenix channel listeners — re-bind when DM user changes
  useEffect(() => {
    if (!currentDmUser || !user) return;

    // Ensure the DM channel is joined
    joinDMChannel(currentDmUser);
    const channel = getChannel(`dm:${currentDmUser}`);
    if (!channel) return;

    const handleNewDirectMessage = async (incoming: any) => {
      const message = normalizeMessage(await hydrateMessageAuthor(incoming));
      const belongsToCurrentConversation =
        (message.sender_id === currentDmUser && message.recipient_id === user.id) ||
        (message.sender_id === user.id && message.recipient_id === currentDmUser);

      if (!belongsToCurrentConversation || message.deleted_at) return;

      setLocalDmMessages(prev => {
        if (prev.some(msg => msg.id === message.id)) {
          return prev;
        }
        return [...prev, message];
      });

      if (shouldStickToBottomRef.current) {
        requestAnimationFrame(() => {
          scrollToBottom('auto');
        });
      }
    };

    const handleMessageUpdate = (message: any) => {
      setLocalDmMessages(prev => prev.map(msg => msg.id === message.id ? applyMessageUpdate(msg, message) : msg));
    };

    const handleMessageDelete = (data: any) => {
      if (data.isDm) {
        setLocalDmMessages(prev => prev.filter(msg => msg.id !== data.id));
      }
    };

    const handleReactionUpdate = (data: any) => {
      if (data.isDm) {
        const container = scrollContainerRef.current;
        const distFromBottom = container
          ? container.scrollHeight - (container.scrollTop + container.clientHeight)
          : 0;

        setLocalDmMessages(prev => prev.map(msg =>
          msg.id === data.messageId ? applyReactionUpdate(msg, data) : msg
        ));

        if (container) {
          requestAnimationFrame(() => {
            container.scrollTop = container.scrollHeight - container.clientHeight - distFromBottom;
          });
        }
      }
    };

    const handleTypingStart = (data: any) => {
      if (!data.roomKey) return;
      useMessageStore.setState(state => {
        const current = state.typingUsers[data.roomKey] || [];
        if (current.some(t => t.userId === data.userId)) return state;
        return { typingUsers: { ...state.typingUsers, [data.roomKey]: [...current, { userId: data.userId, username: data.username }] } };
      });
    };
    const handleTypingStop = (data: any) => {
      if (!data.roomKey) return;
      useMessageStore.setState(state => {
        const current = state.typingUsers[data.roomKey] || [];
        return { typingUsers: { ...state.typingUsers, [data.roomKey]: current.filter(t => t.userId !== data.userId) } };
      });
    };

    const newMsgRef = channel.on('new_message', handleNewDirectMessage);
    const updateRef = channel.on('message_updated', handleMessageUpdate);
    const deleteRef = channel.on('message_deleted', handleMessageDelete);
    const reactionRef = channel.on('reaction_updated', handleReactionUpdate);
    const typingStartRef = channel.on('typing_start', handleTypingStart);
    const typingStopRef = channel.on('typing_stop', handleTypingStop);

    return () => {
      channel.off('new_message', newMsgRef);
      channel.off('message_updated', updateRef);
      channel.off('message_deleted', deleteRef);
      channel.off('reaction_updated', reactionRef);
      channel.off('typing_start', typingStartRef);
      channel.off('typing_stop', typingStopRef);
    };
  }, [currentDmUser, user]);

  useEffect(() => {
    if (isLoading || initialScrollDoneRef.current || localDmMessages.length === 0) return;
    initialScrollDoneRef.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToBottom('auto');
      });
    });
  }, [isLoading, localDmMessages]);

  // Close reaction picker on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (reactionPickerRef.current && !reactionPickerRef.current.contains(e.target as Node)) {
        setReactionPickerMessageId(null);
      }
    };
    if (reactionPickerMessageId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [reactionPickerMessageId]);

  const handleEdit = (messageId: string) => {
    setEditingMessageId(messageId);
  };

  const handleDelete = async (messageId: string) => {
    if (!currentDmUser) return;
    if (window.confirm('Are you sure you want to delete this message?')) {
      setLocalDmMessages(prev => prev.filter(msg => msg.id !== messageId));
      await useMessageStore.getState().deleteMessage(messageId, true, currentDmUser);
    }
  };

  if (!currentDmUser || !user) {
    return (
      <div className="flex-1 flex items-center justify-center bg-bg-primary text-text-secondary">
        Select a friend to start chatting
      </div>
    );
  }

  // Find friend details — fallback to sender info from messages for system users
  const friend = friends.find(f => f.friend_id === currentDmUser)?.friend;
  const systemSender = !friend ? localDmMessages.find(m => m.sender_id === currentDmUser)?.sender : null;
  const isSystemUser = Boolean(systemSender?.is_system && !systemSender?.bot);

  return (
    <div className="flex flex-col h-full bg-bg-primary">
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-2 py-4"
      >
        {isLoading ? (
          <div className="flex flex-col space-y-4 animate-pulse px-4">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-highlight w-10 h-10 flex-shrink-0"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-highlight rounded w-1/4"></div>
                <div className="h-4 bg-highlight rounded w-3/4"></div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-highlight w-10 h-10 flex-shrink-0"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-highlight rounded w-1/3"></div>
                <div className="h-4 bg-highlight rounded w-full"></div>
              </div>
            </div>
          </div>
        ) : localDmMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-text-muted">
            <p>Aucun message</p>
            <p className="text-sm mt-1">Commencez la conversation!</p>
          </div>
        ) : (
          <>
            {isLoadingMore && (
              <div className="text-center mb-2 text-xs text-text-muted">
                Chargement des anciens messages...
              </div>
            )}
            {localDmMessages.map((message, index) => {
              const prevMessage = index > 0 ? localDmMessages[index - 1] : null;
              const isSameAuthor = prevMessage && prevMessage.sender_id === message.sender_id;
	              const timeDiff = prevMessage ? (parseMessageDate(message.created_at).getTime() - parseMessageDate(prevMessage.created_at).getTime()) / 60000 : Infinity;
	              const isGrouped = isSameAuthor && timeDiff < 5;
	              const isCurrentUser = message.sender_id === user.id;
	              const isDeleted = !!message.deleted_at;
	
	              return (
	                <div key={message.id} className={`group relative ${isGrouped ? '' : 'mt-4 first:mt-0'}`}>
	                  {/* Floating action bar */}
	                  {!isDeleted && (
	                  <div className="absolute -top-3 right-4 opacity-0 group-hover:opacity-100 transition-all duration-150 z-10">
                    <div className="flex items-center gap-0.5 bg-bg-secondary border border-border-color rounded-lg shadow-lg px-1 py-0.5">
                      <button 
                        className="w-7 h-7 rounded-md flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-highlight/50 transition-colors"
                        onClick={() => setReactionPickerMessageId(
                          reactionPickerMessageId === message.id ? null : message.id
                        )}
                        title="Réagir"
                      >
                        <Smile className="h-4 w-4" />
                      </button>
                      {isCurrentUser && !message.author?.is_system && (
                        <>
                          <button 
                            className="w-7 h-7 rounded-md flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-highlight/50 transition-colors"
                            onClick={() => handleEdit(message.id)}
                            title="Modifier"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button 
                            className="w-7 h-7 rounded-md flex items-center justify-center text-text-muted hover:text-error hover:bg-error/10 transition-colors"
                            onClick={() => handleDelete(message.id)}
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
	                    </div>
	                  </div>
	                  )}

	                  {/* Emoji picker */}
	                  {reactionPickerMessageId === message.id && !isDeleted && (
	                    <div ref={reactionPickerRef} className="fixed right-4 bottom-24 z-[1000] w-[320px] max-w-[calc(100vw-2rem)]">
                      <EmojiPicker
                        onEmojiClick={(emojiData) => {
                          useMessageStore.getState().addReaction(message.id, emojiData.emoji, true, currentDmUser);
                          setReactionPickerMessageId(null);
                        }}
                        theme={"dark" as any}
	                        width="100%"
                        height={400}
                      />
                    </div>
                  )}

                  <div className={`flex items-start px-4 py-0.5 hover:bg-highlight/10 rounded-md transition-colors`}>
                    {isGrouped ? (
                      <div className="w-10 flex-shrink-0 mr-3 flex items-center justify-center">
                        <span className="text-[10px] text-text-muted opacity-0 group-hover:opacity-100 transition-opacity" title={formatRelativeDate(message.created_at).tooltip}>
                          {format(parseMessageDate(message.created_at), 'HH:mm')}
                        </span>
                      </div>
                    ) : (
                      <UserProfileLink userId={message.sender_id}>
                        <div className="avatar w-10 h-10 flex-shrink-0 mr-3">
                          {message.author?.avatar_url ? (
                            <img 
                              src={message.author.avatar_url} 
                              alt={message.author?.display_name || message.author?.username} 
                              className="w-10 h-10 object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-accent-color text-white flex items-center justify-center rounded-full">
                              {(message.author?.display_name || message.author?.username || 'U').charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                      </UserProfileLink>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      {!isGrouped && (
                        <div className="flex items-center">
                          <UserProfileLink userId={message.sender_id}>
                            <span className="font-medium text-text-primary hover:underline">
                              {message.author?.display_name || message.author?.username || 'Unknown User'}
                            </span>
                          </UserProfileLink>
                          {!!message.author?.bot && <AppBadge className="ml-2" />}
                          <span className="ml-2 text-[11px] text-text-muted" title={formatRelativeDate(message.created_at).tooltip}>
                            {formatRelativeDate(message.created_at).label}
                          </span>
                          {message.edited && (
                            <span className="ml-1.5 text-[10px] text-text-muted">(modifié)</span>
                          )}
                        </div>
                      )}
                      
	                      {isDeleted ? (
	                        <div className={`text-text-muted italic break-words text-[15px] leading-relaxed ${isGrouped ? '' : 'mt-0.5'}`}>
	                          Message supprimé
	                        </div>
	                      ) : editingMessageId === message.id ? (
                        <MessageInput 
                          channelId=""
                          initialValue={message.content}
                          onSend={(content) => {
                            useMessageStore.getState().editMessage(message.id, content, true, currentDmUser);
                            setEditingMessageId(null);
                          }}
                          onCancel={() => setEditingMessageId(null)}
                          isEditing
                        />
                      ) : (
                        <div className={`text-text-primary break-words text-[15px] leading-relaxed ${isGrouped ? '' : 'mt-0.5'}`}>
                          <MessageProcessor content={message.content} />
                        </div>
                      )}

                      {!isDeleted && <DiscordEmbeds embeds={message.embeds} />}

                      {/* Interactive buttons */}
                      {message.buttons && Array.isArray(message.buttons) && message.buttons.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {message.buttons.map((btn: any) => {
                            const styleClasses =
                              btn.style === 'danger'
                                ? 'bg-error text-white hover:bg-error/80'
                                : btn.style === 'secondary'
                                  ? 'bg-bg-tertiary text-text-secondary hover:bg-highlight/50'
                                  : 'bg-accent-color text-black hover:bg-accent-color/80';
                            return (
                              <button
                                key={btn.id}
                                onClick={() => {
                                  if (btn.action === 'navigate' && btn.payload) {
                                    if (btn.payload.startsWith('/settings/')) {
                                      const tab = btn.payload.replace('/settings/', '');
                                      setSettingsInitialTab(tab);
                                      setShowSettingsModal(true);
                                    }
                                  } else if (btn.action === 'url' && btn.payload) {
                                    window.open(btn.payload, '_blank', 'noopener');
                                  }
                                }}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${styleClasses}`}
                              >
                                {btn.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Discord-style reactions */}
	                      {!isDeleted && message.reactions && Object.keys(message.reactions).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {Object.entries(message.reactions).map(([emoji, users]) => {
                            const userArray = users as string[];
                            const isActive = userArray.includes(user.id);
                            return (
                              <button 
                                key={emoji}
                                className={`flex items-center gap-1 rounded-md px-2 py-0.5 text-sm transition-all ${
                                  isActive
                                    ? 'bg-accent-color/15 border border-accent-color/40 text-text-primary'
                                    : 'bg-bg-tertiary/80 hover:bg-highlight border border-transparent hover:border-border-color text-text-secondary'
                                }`}
                                onClick={() => {
                                  if (isActive) {
                                    useMessageStore.getState().removeReaction(message.id, emoji, true, currentDmUser);
                                  } else {
                                    useMessageStore.getState().addReaction(message.id, emoji, true, currentDmUser);
                                  }
                                }}
                              >
                                <span>{emoji}</span>
                                <span className="text-xs font-medium">{userArray.length}</span>
                              </button>
                            );
                          })}
                          <button
                            className="flex items-center justify-center w-7 h-7 rounded-md bg-bg-tertiary/60 hover:bg-highlight text-text-muted hover:text-text-primary transition-all border border-transparent hover:border-border-color"
                            onClick={() => setReactionPickerMessageId(message.id)}
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
      
      {!isSystemUser && (
      <div className="px-4 py-3">
        <TypingIndicator roomKey={`dm:${[user!.id, currentDmUser].sort().join('-')}`} />
        <MessageInput 
          channelId=""
          isDm={true}
          recipientId={currentDmUser}
          onSend={(content) => {
            useMessageStore.getState().sendDirectMessage(content, currentDmUser);
          }}
        />
      </div>
      )}
      
      {showSettingsModal && (
        <UserSettingsModal initialTab={settingsInitialTab} onClose={() => { setShowSettingsModal(false); setSettingsInitialTab(undefined); }} />
      )}
    </div>
  );
};

export default DirectMessageChat;
